"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useMutation } from "@tanstack/react-query";
import { parseAsString, useQueryState } from "nuqs";
import {
  ArrowUpRight,
  FileSpreadsheet,
  Loader2,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { Button } from "@ocr-monorepo/ui/components/button";
import { publicErrorMessage } from "@ocr-monorepo/shared";
import { PdfToCsvWorkspace } from "@/components/conversions/pdf-to-csv-workspace";
import { MarketingDocumentDemo } from "@/components/marketing-document-demo";
import { TurnstileWidget } from "@/components/turnstile-widget";
import { client as authClient, useSession } from "@/lib/auth-client";
import { useTRPC } from "@/lib/trpc";

export function PdfToCsvConverter() {
  const trpc = useTRPC();
  const { data: session, isPending: sessionPending, refetch } = useSession();
  const [conversionId, setConversionId] = useQueryState(
    "conversion",
    parseAsString.withOptions({ shallow: true, clearOnDefault: true }),
  );
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isAnonymous = Boolean(
    (session?.user as { isAnonymous?: boolean } | undefined)?.isAnonymous,
  );

  useEffect(() => {
    if (sessionPending || session) return;
    void authClient.signIn.anonymous().then(async (result) => {
      if (result.error) {
        setError(publicErrorMessage("system_error"));
        return;
      }
      await refetch();
    });
  }, [refetch, session, sessionPending]);

  const createConversion = useMutation(
    trpc.converters.create.mutationOptions({
      onSuccess: async ({ conversion }) => {
        await setConversionId(conversion.id);
      },
      onError: (mutationError) => {
        const message = mutationError.message.includes("ANONYMOUS_CONVERSION_LIMIT")
          ? "This network has reached today’s temporary-conversion limit. Create an account to continue."
          : mutationError.message.includes("TURNSTILE")
            ? "Security verification expired. Please verify again."
            : publicErrorMessage("system_error");
        setError(message);
        setTurnstileToken(null);
      },
    }),
  );

  if (conversionId) {
    return (
      <div className="h-[680px] overflow-hidden rounded-xl border border-border bg-background shadow-[0_20px_60px_-36px_rgba(0,0,0,0.35)] lg:h-[720px]">
        <PdfToCsvWorkspace />
      </div>
    );
  }

  const canStart = Boolean(session && (!isAnonymous || turnstileToken));

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-[0_20px_60px_-36px_rgba(0,0,0,0.35)]">
      <div className="flex min-h-11 items-center justify-between gap-3 border-b border-border px-4 py-2">
        <div className="flex items-center gap-2.5">
          <span className="flex size-6 items-center justify-center rounded border border-border bg-background text-primary">
            <FileSpreadsheet className="size-3.5" />
          </span>
          <div>
            <p className="text-xs font-semibold">Live PDF to CSV workspace</p>
            <p className="text-[9px] text-muted-foreground">Upload a real document and download the result</p>
          </div>
        </div>
        <span className="hidden items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground sm:flex">
          <ShieldCheck className="size-3 text-primary" /> Protected by Cloudflare
        </span>
      </div>

      <MarketingDocumentDemo details={
        <aside className="flex min-h-0 flex-col border-b border-border bg-muted/15 p-6 xl:border-r xl:border-b-0 lg:p-7">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-primary">Use the real converter</p>
            <h2 className="mt-3 text-xl font-semibold tracking-tight">Turn your own PDF into structured CSV</h2>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">Open a protected workspace, upload a real document, and download the extracted rows. The table and document preview stay together throughout the conversion.</p>
          </div>
          <dl className="mt-6 divide-y divide-border border-y border-border text-[11px]">
            <div className="flex items-center justify-between py-3"><dt className="text-muted-foreground">Files</dt><dd className="font-semibold">Up to 3 PDFs</dd></div>
            <div className="flex items-center justify-between py-3"><dt className="text-muted-foreground">Pages</dt><dd className="font-semibold">5 total</dd></div>
            <div className="flex items-center justify-between py-3"><dt className="text-muted-foreground">Expires</dt><dd className="font-semibold">After 2 hours</dd></div>
          </dl>
          <p className="mt-3 text-[10px] leading-relaxed text-muted-foreground">No account required. The five-page allowance is shared across every PDF in this temporary session.</p>
          <div className="mt-auto pt-5">
            <div className="min-h-16">
              {sessionPending || (!session && !error) ? (
                <div className="flex items-center gap-2 py-4 text-[10px] text-muted-foreground"><Loader2 className="size-3 animate-spin" /> Preparing a secure session…</div>
              ) : isAnonymous ? (
                <TurnstileWidget action="pdf-to-csv" size="normal" onSuccess={(token) => { setTurnstileToken(token); setError(null); }} onExpire={() => setTurnstileToken(null)} onError={() => { setTurnstileToken(null); setError("Security verification failed. Please try again."); }} />
              ) : (
                <p className="py-4 text-[10px] text-muted-foreground">Your signed-in account is ready.</p>
              )}
            </div>
            {error && <p className="mb-3 text-[10px] leading-relaxed text-destructive">{error}</p>}
            <Button className="w-full" disabled={!canStart || createConversion.isPending} onClick={() => { setError(null); createConversion.mutate({ type: "pdf_to_csv", name: "Website PDF to CSV", turnstileToken: isAnonymous ? turnstileToken ?? undefined : undefined }); }}>
              {createConversion.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Upload className="mr-2 size-4" />}
              Open converter
            </Button>
            <Link href="/getting-started/create-account?source=pdf-to-csv-demo" className="mt-4 flex items-center justify-center text-xs font-semibold text-foreground transition-colors hover:text-primary">Need more pages? Create a free account <ArrowUpRight className="ml-1.5 size-3.5" /></Link>
          </div>
        </aside>
      } />
    </div>
  );
}
